/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box, Card, Container, Heading, Separator, SimpleGrid, VStack } from 'chakra-v3';
import { Text } from '../../components/Text';

interface InsightCardProps {
  number: string;
  highlight: string;
  description: string;
}

const InsightCard: React.FC<InsightCardProps> = ({ number, highlight, description }) => (
  <Card.Root
    unstyled
    borderRadius="4px"
    minH={{ base: '180px', sm: '190px', md: '200px' }}
  >
    <Card.Body p="24px" gap="16px">
      {/* Number + Separator */}
      <Card.Header p="0">
        <VStack gap="8px" alignItems="flex-start" w="100%">
          <Heading
            as="h3"
            fontFamily="heading"
            fontSize="20px"
            fontWeight="normal"
            lineHeight="normal"
            letterSpacing="-1px"
            color="#0A313C"
          >
            {number}
          </Heading>
          <Separator
            orientation="horizontal"
            w="100%"
            borderColor="rgba(10, 49, 60, 0.2)"
            mb="5px"
          />
        </VStack>
      </Card.Header>

      {/* Description */}
      <Card.Description asChild>
        <Text
          fontFamily="body"
          fontSize="16px"
          fontWeight="normal"
          lineHeight="1.6"
          color="#0A313C"
        >
          <strong>{highlight}</strong>{' '}
          {description}
        </Text>
      </Card.Description>
    </Card.Body>
  </Card.Root>
);

const insights = [
  {
    number: '01',
    highlight: 'Cultural competence separates winners from losers.',
    description:
      'Highly-rated responses use fluent, natural language and are grounded in Indian socio-economic realities and institutional contexts. Low-scoring answers stumble over basic cultural nuances.',
  },
  {
    number: '02',
    highlight: 'Low-resourced languages are systematically left behind.',
    description:
      'Model performance drops steadily from high-resourced to medium and low-resourced languages. Assamese-the least-resourced language tested-sees not only the lowest average scores but also the widest performance gap between best and worst models.',
  },
  {
    number: '03',
    highlight: 'LLM-judges are not ready to replace humans.',
    description:
      'Poor alignment with human evaluators proves that automated judges cannot substitute human judgment. LLM-judges require application-specific calibration, continuous grounding in human evaluation, and cautious deployment.',
  },
  {
    number: '04',
    highlight: 'Meaningful evaluation demands an uncompromising approach.',
    description:
      'Essential components include extensive community input, comprehensive coverage spanning diverse languages and contexts, a rigorous mix of quantitative and qualitative methods, and real-world scale.',
  },
];

const InsightsAndRecommendations: React.FC = () => {
  return (
    <Box
      as="section"
      w="100%"
      bg="radial-gradient(154.71% 100% at 50% 0%, #FAF7F4 0%, #F2EDE7 100%)"
      px={{ base: '16px', sm: '24px', md: '32px', lg: '40px', xl: '40px' }}
      py={{ base: '48px', sm: '60px', md: '80px', lg: '100px', xl: '120px' }}
    >
      <Container maxW="1440px" p="0">
        <VStack gap="24px" alignItems="center" w="100%">
          {/* Section Heading */}
          <Heading
            as="h2"
            fontFamily="heading"
            fontSize={{ base: '36px', sm: '40px', md: '52px', lg: '60px', xl: '72px' }}
            fontWeight="300"
            lineHeight="normal"
            letterSpacing="-3px"
            color="#0A313C"
            textAlign="center"
            w="100%"
          >
            Key Takeaways
          </Heading>

          <Text
            fontFamily="body"
            fontSize={{ base: '18px', sm: '20px', md: '22px', lg: '24px' }}
            fontWeight="normal"
            lineHeight="1.4"
            letterSpacing="0.12px"
            color="#0A313C"
            textAlign="center"
            w="100%"
          >
            Samiksha seeks to deliver actionable insights to model builders and policymakers. Our key findings are as follows:
          </Text>

          {/* Insight Cards */}
          <VStack gap="24px" alignItems="center" w="100%">
            <SimpleGrid
              columns={{ base: 1, md: 2, lg: insights.length }}
              gap="24px"
              w="100%"
            >
              {insights.map((insight) => (
                <InsightCard
                  key={insight.number}
                  number={insight.number}
                  highlight={insight.highlight}
                  description={insight.description}
                />
              ))}
            </SimpleGrid>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default InsightsAndRecommendations;
